import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useLeagueStore } from '../../lib/leagueStore';
import { useUserStore } from '../../lib/userStore';
import toast from 'react-hot-toast';

interface CreateQuestionModalProps {
  show: boolean;
  onHide: () => void;
  onQuestionCreated: () => void;
  categories: Array<{ value: string; label: string }>;
  priorities: Array<{ value: string; label: string; color: string }>;
}

export function CreateQuestionModal({ 
  show, 
  onHide, 
  onQuestionCreated, 
  categories, 
  priorities 
}: CreateQuestionModalProps) {
  const { selectedLeague } = useLeagueStore();
  const { user_id } = useUserStore();
  const [question, setQuestion] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedLeague?.id) {
      toast.error('Aucune ligue sélectionnée');
      return;
    }

    if (!user_id) {
      toast.error('Utilisateur non connecté');
      return;
    }

    if (!question.trim()) {
      toast.error('Veuillez saisir votre question');
      return;
    }

    if (question.length < 5) {
      toast.error('La question doit contenir au moins 5 caractères');
      return;
    }

    if (question.length > 1000) {
      toast.error('La question ne peut pas dépasser 1000 caractères');
      return;
    }

    try {
      setLoading(true);
      
      // Insertion directe dans la table
      const { data, error } = await supabase
        .from('faq_questions')
        .insert({
          league_id: selectedLeague.id,
          user_id: user_id,
          question: question.trim(),
          category: category,
          priority: priority,
          status: 'open',
          is_pinned: false,
          views_count: 0
        })
        .select();

      if (error) throw error;

      // Reset form
      setQuestion('');
      setCategory('general');
      setPriority('normal');
      
      onQuestionCreated();
      toast.success('Question créée avec succès!');
    } catch (error: any) {
      console.error('Error creating question:', error);
      toast.error(error.message || 'Erreur lors de la création de la question');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setQuestion('');
      setCategory('general');
      setPriority('normal');
      onHide();
    }
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-question-circle me-2"></i>
              Poser une nouvelle question
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              disabled={loading}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">Question *</label>
                <textarea
                  className="form-control"
                  rows={4}
                  value={question}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)}
                  placeholder="Décrivez votre question de manière claire et détaillée..."
                  maxLength={1000}
                  disabled={loading}
                />
                <div className="form-text">
                  {question.length}/1000 caractères (minimum 5)
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Catégorie</label>
                    <select
                      className="form-select"
                      value={category}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCategory(e.target.value)}
                      disabled={loading}
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Priorité</label>
                    <select
                      className="form-select"
                      value={priority}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPriority(e.target.value)}
                      disabled={loading}
                    >
                      {priorities.map(prio => (
                        <option key={prio.value} value={prio.value}>
                          {prio.label}
                        </option>
                      ))}
                    </select>
                    <div className="form-text">
                      Choisissez "Urgente" seulement pour les problèmes critiques
                    </div>
                  </div>
                </div>
              </div>

              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Conseils :</strong>
                <ul className="mb-0 mt-2">
                  <li>Soyez aussi précis que possible dans votre question</li>
                  <li>Vérifiez si votre question n'a pas déjà été posée</li>
                  <li>Le commissaire recevra une notification de votre question</li>
                </ul>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Annuler
              </button>
              <button 
                type="submit"
                className="btn btn-primary"
                disabled={loading || !question.trim() || question.length < 5}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Création...
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane me-2"></i>
                    Poser la question
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
